import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { APPROVAL } from "pages/Sales/Register/utils/vars"
import { DetailCustomerQuotation } from "pages/Sales/Transaction/CustomerQuotation/types"
import { calculateSummaryCQ } from "pages/Sales/Transaction/CustomerQuotation/utils/functions"
import { mapCustomerQuotationDetail } from "./mapCustomerQuotationDetail"

type ExportExcelQuotation = {
  data: DetailCustomerQuotation[]
  dateFilter: string
  companyName?: string
}

export const exportExcelQuotation = async ({
  data,
  dateFilter
}: ExportExcelQuotation) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "QUOTATION ID#",
    "REF SO ID#",
    "DATE",
    "SALES",
    "CUSTOMER",
    "LOCATION",
    "STATUS",
    "QTY. ORDER",
    "TOTAL SALES",
  ]
  worksheet.views = [{ showGridLines: false }]

  // Title
  makeTitle(worksheet, dateFilter)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF000000" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  makeRows(data, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `quotation-${dateFilter}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

const makeTitle = (worksheet: Worksheet, dateFilter: string) => {
  worksheet.mergeCells(1, 1, 1, 10)
  const purchaseCell = worksheet.getCell(1, 1)
  purchaseCell.value = "SALES"
  purchaseCell.font = { bold: true, size: 15 }
  purchaseCell.alignment = {
    vertical: "middle",
    horizontal: "center"
  }

  worksheet.mergeCells(2, 1, 2, 10)
  const registerCell = worksheet.getCell(2, 1)
  registerCell.value = "REGISTER QUOTATION"
  registerCell.font = { bold: true, size: 15 }
  registerCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }

  worksheet.mergeCells(3, 1, 3, 10)
  const dateCell = worksheet.getCell(3, 1)
  dateCell.value = `DATE: ${dateFilter}`
  dateCell.font = { bold: true, size: 13 }
  dateCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }

  worksheet.getColumn(2).width = 20
}

function makeRows(
  data: ExportExcelQuotation["data"],
  worksheet: Worksheet
) {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    const sumQtyOrder = item.detail.reduce((total, item) => total + item.qty_order, 0);
    const mapDetail = mapCustomerQuotationDetail(item.detail)
    const summary = calculateSummaryCQ({
      detail: mapDetail,
      freight: item.freight
    })

    itemRow = worksheet.addRow([
      index + 1,
      item.trx_code,
      "-",
      moment(item.transaction_date).format("DD/MM/YYYY"),
      item.employee.employee_name,
      item.customer.customer_name,
      item.location.location_name,
      APPROVAL[item.approve_status],
      sumQtyOrder,
      summary.totalAmount
    ])

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })
  })
}
